/**
 * Base documentaire d'une formation (recette du 31/10/2026 : « fais une recherche Web globale et façonne une base de
 * données et lien consultable pour la suite »).
 *
 * Une seule recherche web approfondie, au moment du dossier d'enjeux, alimente la base ; parcours, tests, supports et
 * visuels s'appuient ensuite sur elle (sans nouvelle recherche) et citent leurs sources par leur numéro « S1, S2… ».
 *
 * Règle de vérité : une URL n'entre dans la base QUE si l'API l'a renvoyée (résultat de recherche ou citation). Le
 * modèle classe et résume les sources ; il ne peut pas en inventer une. L'extrait cité par l'API (`cited_text`) prime
 * sur celui relevé par le modèle.
 *
 * Module pur.
 */

export const TYPES_SOURCE = ["reglementaire", "metier", "donnees_chiffrees", "pedagogie"] as const;
export type TypeSource = (typeof TYPES_SOURCE)[number];

export const LIBELLES_TYPE_SOURCE: Record<TypeSource, string> = {
  reglementaire: "Réglementaire",
  metier: "Métier",
  donnees_chiffrees: "Données chiffrées",
  pedagogie: "Pédagogie",
};

/** Source telle que l'API l'a renvoyée (résultat de recherche web et/ou citation). */
export interface SourceWeb {
  url: string;
  titre: string;
  /** `page_age` du résultat de recherche (date de mise à jour indiquée par le site), s'il est connu. */
  page_age?: string | null;
  /** Textes cités (`cited_text`, 150 caractères au plus chacun). */
  extraits?: string[];
}

/** Source retenue pour la base (avant enregistrement). */
export interface SourceRetenue {
  url: string;
  titre: string;
  editeur: string;
  publie_le: string;
  type: TypeSource;
  resume: string;
  extrait: string;
  extrait_origine: "citation_api" | "ia" | "";
}

/** Source enregistrée, telle que les consignes et les documents la citent. */
export interface SourceBase {
  rang: number;
  titre: string;
  url: string;
  editeur: string;
  publie_le: string;
  type: TypeSource;
  resume: string;
  extrait: string;
}

export const MAX_SOURCES = 15;

/** Adresse normalisée pour comparer : sans fragment, sans « / » final, schéma et hôte en minuscules. */
export function normaliserUrl(url: string): string | null {
  try {
    const u = new URL(url.trim());
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    u.hash = "";
    return `${u.protocol}//${u.host.toLowerCase()}${u.pathname.replace(/\/+$/, "")}${u.search}`;
  } catch {
    return null;
  }
}

const texte = (x: unknown, max: number) => (typeof x === "string" ? x.replace(/\s+/g, " ").trim().slice(0, max) : "");

/** Éditeur déduit de l'adresse quand le modèle ne l'a pas donné (« legifrance.gouv.fr »). */
export const editeurDepuisUrl = (url: string) => {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/**
 * Fusionne la classification proposée par le modèle (titre, éditeur, date, type, résumé, extrait) avec les sources
 * RÉELLEMENT renvoyées par l'API. Une proposition dont l'URL n'a pas été renvoyée par l'API est écartée ; une source de
 * l'API non classée par le modèle est gardée (type « métier », sans résumé).
 */
export function fusionnerSources(proposees: unknown, web: readonly SourceWeb[], max = MAX_SOURCES): { retenues: SourceRetenue[]; ecartees: string[] } {
  const parUrl = new Map<string, SourceWeb>();
  for (const w of web) {
    const n = normaliserUrl(w.url);
    if (n && !parUrl.has(n)) parUrl.set(n, w);
  }
  const retenues = new Map<string, SourceRetenue>();
  const ecartees: string[] = [];
  for (const brut of Array.isArray(proposees) ? proposees : []) {
    const p = (brut ?? {}) as Record<string, unknown>;
    const n = typeof p.url === "string" ? normaliserUrl(p.url) : null;
    const w = n ? parUrl.get(n) : undefined;
    if (!n || !w) {
      if (typeof p.url === "string") ecartees.push(p.url.slice(0, 200));
      continue;
    }
    if (retenues.has(n)) continue;
    const citations = (w.extraits ?? []).map((x) => x.trim()).filter(Boolean);
    const extraitIa = texte(p.extrait_cle ?? p.extrait, 600);
    retenues.set(n, {
      url: w.url,
      titre: texte(p.titre, 300) || texte(w.titre, 300) || w.url,
      editeur: texte(p.editeur, 120) || editeurDepuisUrl(w.url),
      publie_le: texte(p.date_publication ?? p.publie_le, 40) || texte(w.page_age, 40),
      type: (TYPES_SOURCE as readonly string[]).includes(String(p.type)) ? (p.type as TypeSource) : "metier",
      resume: texte(p.resume, 600),
      extrait: citations.length ? citations.join(" … ").slice(0, 900) : extraitIa,
      extrait_origine: citations.length ? "citation_api" : extraitIa ? "ia" : "",
    });
  }
  for (const [n, w] of parUrl) {
    if (retenues.has(n)) continue;
    const citations = (w.extraits ?? []).map((x) => x.trim()).filter(Boolean);
    retenues.set(n, { url: w.url, titre: texte(w.titre, 300) || w.url, editeur: editeurDepuisUrl(w.url), publie_le: texte(w.page_age, 40), type: "metier", resume: "", extrait: citations.join(" … ").slice(0, 900), extrait_origine: citations.length ? "citation_api" : "" });
  }
  // Les sources classées par le modèle d'abord (il les a jugées utiles), puis les autres résultats, dans la limite.
  return { retenues: [...retenues.values()].slice(0, max), ecartees };
}

/** La base, pour les consignes : chaque source numérotée « [S1] », son type, son résumé et son extrait. */
export function decrireSources(sources: readonly SourceBase[]): string {
  if (sources.length === 0) return "";
  return [
    "BASE DE SOURCES DE LA FORMATION (issue d'une recherche documentaire ; cite les sources que tu utilises par leur numéro, ex. [S2] ; n'utilise aucun chiffre, texte ou date qui n'y figure pas) :",
    ...sources.map((s) =>
      [`[S${s.rang}] (${LIBELLES_TYPE_SOURCE[s.type]}) ${s.titre}${s.editeur ? ` — ${s.editeur}` : ""}${s.publie_le ? `, ${s.publie_le}` : ""}`, s.resume ? `   Résumé : ${s.resume}` : "", s.extrait ? `   Extrait : « ${s.extrait} »` : ""].filter(Boolean).join("\n"),
    ),
  ].join("\n");
}

/** Rangs cités dans une réponse (`[1, "S3", 4]`), limités à ceux de la base, sans doublon. */
export function rangsCites(brut: unknown, sources: readonly SourceBase[]): number[] {
  const connus = new Set(sources.map((s) => s.rang));
  const rangs = (Array.isArray(brut) ? brut : [])
    .map((x) => (typeof x === "number" ? x : Number(String(x).replace(/^\[?S/i, "").replace(/\]$/, ""))))
    .filter((n) => Number.isInteger(n) && connus.has(n));
  return [...new Set(rangs)];
}

/** Rangs cités « [S2] » dans un texte libre. */
export function rangsDansTexte(texteLibre: string, sources: readonly SourceBase[]): number[] {
  return rangsCites([...texteLibre.matchAll(/\[S(\d{1,2})\]/g)].map((m) => Number(m[1])), sources);
}

/**
 * Nombres présents dans un texte (« 12,5 % », « 1 234 », « 3.4 ») : sert à vérifier qu'un graphique ne contient que des
 * chiffres relevés dans l'extrait de sa source.
 */
export function nombresDuTexte(t: string): number[] {
  const res: number[] = [];
  for (const m of t.matchAll(/\d{1,3}(?:[\u00a0\u202f ]\d{3})+(?:[.,]\d+)?|\d+(?:[.,]\d+)?/g)) {
    const n = Number(m[0].replace(/[\u00a0\u202f ]/g, "").replace(",", "."));
    if (Number.isFinite(n)) res.push(n);
  }
  return res;
}

export function valeurPresente(valeur: number, t: string): boolean {
  return nombresDuTexte(t).some((n) => Math.abs(n - valeur) < 1e-9);
}
